import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export type ProductStatus = "disponivel" | "beta" | "em_desenvolvimento";

export const PRODUCT_STATUS_LABEL: Record<ProductStatus, string> = {
  disponivel: "Disponível",
  beta: "Beta",
  em_desenvolvimento: "Em desenvolvimento",
};

export const PRODUCT_STATUS_CLASS: Record<ProductStatus, string> = {
  disponivel: "bg-ok-soft text-ok",
  beta: "bg-warn-soft text-warn",
  em_desenvolvimento: "bg-muted text-muted-foreground",
};

export interface ProductCatalogItem {
  id: string;
  slug: string;
  name: string;
  status: ProductStatus;
  description: string | null;
  pricing: string | null;
  features: string[];
  stack: string | null;
  image_url: string | null;
  included: string[];
  deadline: string | null;
  display_order: number;
  created_at: string;
  updated_at: string;
}

// Hook público — lista o catálogo de produtos prontos, ordenado para exibição.
export function useProductsCatalog() {
  return useQuery({
    queryKey: ["products-catalog"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("products_catalog" as never)
        .select("*")
        .order("display_order", { ascending: true });

      if (error) throw error;
      return data as unknown as ProductCatalogItem[];
    },
  });
}

// Hook público — busca um produto específico pelo slug (para a página de detalhe).
export function useProductCatalogItem(slug: string | undefined) {
  return useQuery({
    queryKey: ["products-catalog", slug],
    queryFn: async () => {
      if (!slug) return null;

      const { data, error } = await supabase
        .from("products_catalog" as never)
        .select("*")
        .eq("slug", slug)
        .maybeSingle();

      if (error) throw error;
      return data as unknown as ProductCatalogItem | null;
    },
    enabled: !!slug,
  });
}

export interface ProductCatalogInput {
  slug: string;
  name: string;
  status: ProductStatus;
  description: string | null;
  pricing: string | null;
  features: string[];
  stack: string | null;
  image_url: string | null;
  included: string[];
  deadline: string | null;
  display_order: number;
}

// Hook admin — lista todos os produtos (inclui os não disponíveis), para o CRUD.
export function useAllProductsCatalog() {
  return useQuery({
    queryKey: ["products-catalog", "admin"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("products_catalog" as never)
        .select("*")
        .order("display_order", { ascending: true })
        .order("name", { ascending: true });

      if (error) throw error;
      return data as unknown as ProductCatalogItem[];
    },
  });
}

function useInvalidateCatalog() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ["products-catalog"] });
}

export function useCreateProduct() {
  const invalidate = useInvalidateCatalog();
  return useMutation({
    mutationFn: async (input: ProductCatalogInput) => {
      const { data, error } = await supabase
        .from("products_catalog" as never)
        .insert(input as never)
        .select()
        .single();
      if (error) throw error;
      return data as unknown as ProductCatalogItem;
    },
    onSuccess: () => {
      invalidate();
      toast.success("Produto criado");
    },
    onError: (e: Error) => toast.error(e.message || "Erro ao criar produto"),
  });
}

export function useUpdateProduct() {
  const invalidate = useInvalidateCatalog();
  return useMutation({
    mutationFn: async ({ id, ...input }: Partial<ProductCatalogInput> & { id: string }) => {
      const { data, error } = await supabase
        .from("products_catalog" as never)
        .update(input as never)
        .eq("id", id)
        .select()
        .single();
      if (error) throw error;
      return data as unknown as ProductCatalogItem;
    },
    onSuccess: () => {
      invalidate();
      toast.success("Produto atualizado");
    },
    onError: (e: Error) => toast.error(e.message || "Erro ao atualizar produto"),
  });
}

export function useDeleteProduct() {
  const invalidate = useInvalidateCatalog();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("products_catalog" as never)
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
      toast.success("Produto excluído");
    },
    onError: (e: Error) => toast.error(e.message || "Erro ao excluir produto"),
  });
}
